import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CATEGORIES = ['all', 'protein', 'carbs', 'fats', 'vegetables', 'general'];

const MacroBadge = ({ label, value, color }) => (
  <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${color}`}>
    {label}: {value}g
  </span>
);

const FoodLibraryPanel = ({ foodItems, customFoodItems, onAddFood, onAddCustomFood }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({
    name: '', serving_size_description: '1 serving',
    calories_per_serving: '', protein_g_per_serving: '',
    carbs_g_per_serving: '', fats_g_per_serving: '', notes: ''
  });
  const [activeTab, setActiveTab] = useState('library');

  const filtered = (activeTab === 'library' ? foodItems : customFoodItems)?.filter(f => {
    const matchSearch = f?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchCat = category === 'all' || f?.category === category;
    return matchSearch && matchCat;
  });

  const handleCustomSubmit = (e) => {
    e?.preventDefault();
    onAddCustomFood({
      ...customForm,
      calories_per_serving: parseFloat(customForm?.calories_per_serving) || 0,
      protein_g_per_serving: parseFloat(customForm?.protein_g_per_serving) || 0,
      carbs_g_per_serving: parseFloat(customForm?.carbs_g_per_serving) || 0,
      fats_g_per_serving: parseFloat(customForm?.fats_g_per_serving) || 0,
    });
    setCustomForm({ name: '', serving_size_description: '1 serving', calories_per_serving: '', protein_g_per_serving: '', carbs_g_per_serving: '', fats_g_per_serving: '', notes: '' });
    setShowCustomForm(false);
  };

  const categoryColors = {
    protein: 'bg-blue-500/10 text-blue-400',
    carbs: 'bg-amber-500/10 text-amber-400',
    fats: 'bg-orange-500/10 text-orange-400',
    vegetables: 'bg-green-500/10 text-green-400',
    general: 'bg-muted text-muted-foreground',
  };

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Apple" size={16} className="text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground">Food Library</h2>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowCustomForm(v => !v)}>
            <Icon name="Plus" size={14} className="mr-1" />
            Custom
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 mb-3">
          {['library', 'custom']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'library' ? 'Library' : 'My Foods'}
            </button>
          ))}
        </div>

        <Input
          placeholder="Search foods..."
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          className="mb-2"
        />

        <div className="flex gap-1 flex-wrap">
          {CATEGORIES?.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`text-xs px-2 py-1 rounded-full transition-all ${
                category === cat
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {cat?.charAt(0)?.toUpperCase() + cat?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Custom Food Form */}
      <AnimatePresence>
        {showCustomForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-border"
          >
            <form onSubmit={handleCustomSubmit} className="p-4 space-y-2">
              <p className="text-xs font-semibold text-foreground mb-2">Add Custom Food</p>
              <Input
                placeholder="Food name *"
                value={customForm?.name}
                onChange={e => setCustomForm(p => ({ ...p, name: e?.target?.value }))}
                required
              />
              <Input
                placeholder="Serving size (e.g. 100g)"
                value={customForm?.serving_size_description}
                onChange={e => setCustomForm(p => ({ ...p, serving_size_description: e?.target?.value }))}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Calories" type="number" value={customForm?.calories_per_serving} onChange={e => setCustomForm(p => ({ ...p, calories_per_serving: e?.target?.value }))} />
                <Input placeholder="Protein (g)" type="number" value={customForm?.protein_g_per_serving} onChange={e => setCustomForm(p => ({ ...p, protein_g_per_serving: e?.target?.value }))} />
                <Input placeholder="Carbs (g)" type="number" value={customForm?.carbs_g_per_serving} onChange={e => setCustomForm(p => ({ ...p, carbs_g_per_serving: e?.target?.value }))} />
                <Input placeholder="Fats (g)" type="number" value={customForm?.fats_g_per_serving} onChange={e => setCustomForm(p => ({ ...p, fats_g_per_serving: e?.target?.value }))} />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="flex-1">Save Food</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowCustomForm(false)}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Food List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <p className="text-xs text-muted-foreground px-1">{filtered?.length} items</p>
        {filtered?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Search" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No foods found</p>
          </div>
        ) : (
          filtered?.map(food => (
            <motion.div
              key={food?.id}
              className="p-3 rounded-lg border border-border bg-muted/30 hover:border-primary/40 hover:bg-muted/60 transition-all group"
              whileHover={{ scale: 1.01 }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{food?.name}</p>
                    {food?.category && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${categoryColors?.[food?.category] || categoryColors?.general}`}>
                        {food?.category}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-1.5">{food?.serving_size_description}</p>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs font-semibold text-primary">{food?.calories_per_serving} kcal</span>
                    <MacroBadge label="P" value={food?.protein_g_per_serving} color="text-blue-400 bg-blue-500/10" />
                    <MacroBadge label="C" value={food?.carbs_g_per_serving} color="text-amber-400 bg-amber-500/10" />
                    <MacroBadge label="F" value={food?.fats_g_per_serving} color="text-orange-400 bg-orange-500/10" />
                  </div>
                </div>
                <button
                  onClick={() => onAddFood(food)}
                  className="w-7 h-7 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground flex items-center justify-center transition-all flex-shrink-0 opacity-0 group-hover:opacity-100"
                >
                  <Icon name="Plus" size={14} />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default FoodLibraryPanel;
